import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { EMPTY } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';

import { CaseAcquisitionStatus, CasesService, MyCasesData } from '../../service/cases.service';
import { MyCasesModalComponent } from './modal/my-cases-modal.component';
import { StatusConfirmationComponent } from './status-confirmation/status-confirmation.component';
import { MoveEvent } from './kanban-column/kanban-column.component';

type LeadsGroups = {
  column: CaseAcquisitionStatus;
  leads: MyCasesData[];
};

type MoveCtx = {
  fromStatus: CaseAcquisitionStatus;
  toStatus: CaseAcquisitionStatus;
  fromIndex: number;
  toIndex: number;
};

@Component({
  selector: 'app-my-cases',
  templateUrl: './my-cases.component.html',
  styleUrls: ['./my-cases.component.scss'],
})
export class MyCasesComponent implements OnInit {
  leads = new Map<CaseAcquisitionStatus, MyCasesData[]>();
  loading = true;

  constructor(
    private readonly service: CasesService,
    private readonly dialog: MatDialog,
    private readonly snack: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.configureColumns();
    this.myCasesList();
  }

  get leadList(): LeadsGroups[] {
    return Array.from(this.leads.entries()).map(([column, leads]) => ({
      column,
      leads,
    }));
  }

  get dropListIds(): string[] {
    return this.leadList.map((g) => `drop-${g.column}`);
  }

  onMoved(ev: MoveEvent) {
    const fromStatus = ev.previousStatus;
    const toStatus = ev.currentStatus;

    if (fromStatus === toStatus) {
      moveItemInArray(this.leads.get(toStatus) ?? [], ev.previousIndex, ev.currentIndex);
      this.leads = new Map(this.leads);
      this.leads.set(toStatus, [...(this.leads.get(toStatus) ?? [])]);
      return;
    }

    const ctx: MoveCtx = {
      fromStatus: ev.previousStatus,
      toStatus: ev.currentStatus,
      fromIndex: ev.previousIndex,
      toIndex: ev.currentIndex,
    };

    this.applyMove(ctx);

    const ref = this.dialog.open(StatusConfirmationComponent, {
      minWidth: '420px',
      disableClose: true,
      data: {
        leadId: ev.lead.id,
        lastObservation: ev.lead.lastObservation ?? '',
        previousStatus: ctx.fromStatus,
        currentStatus: ctx.toStatus,
      },
    });

    ref.afterClosed().subscribe((result?: { lastObservation: string } | false) => {
      // Cancelou
      if (!result) {
        this.revertMove(ctx);
        return;
      }

      this.patchLeadInColumn(ctx.toStatus, ev.lead.id, {
        lastObservation: !result.lastObservation ? ev.lead.lastObservation : result.lastObservation,
        lastObservationStatus: ctx.toStatus,
      });
    });
  }

  private applyMove(ctx: MoveCtx) {
    const { fromStatus, toStatus, fromIndex, toIndex } = ctx;

    const map = new Map(this.leads);
    const fromArr = [...(map.get(fromStatus) ?? [])];
    const toArr = fromStatus === toStatus ? fromArr : [...(map.get(toStatus) ?? [])];

    if (fromStatus === toStatus) {
      moveItemInArray(toArr, fromIndex, toIndex);
      map.set(toStatus, toArr);
    } else {
      transferArrayItem(fromArr, toArr, fromIndex, toIndex);
      map.set(fromStatus, fromArr);
      map.set(toStatus, toArr);

      const movedLead = toArr[toIndex];
      if (movedLead) movedLead.status = toStatus;
    }

    this.leads = map;
  }

  private revertMove(ctx: MoveCtx) {
    this.applyMove({
      fromStatus: ctx.toStatus,
      toStatus: ctx.fromStatus,
      fromIndex: ctx.toIndex,
      toIndex: ctx.fromIndex,
    });
  }

  async showCaseDetails(caseId: number) {
    this.dialog.open(MyCasesModalComponent, {
      data: { caseId },
      width: '700px',
    });
  }

  archiveCase(caseId: number) {
    this.loading = true;
    this.service.changeStatusArchivedCase(caseId).subscribe({
      next: (data) => {
        this.snack.open(data.message, 'OK', { duration: 2000 });
        this.myCasesList();
      },
      error: (err) => console.log('Erro ao arquivar caso', err),
    });
  }

  trackByGroup(group: LeadsGroups) {
    return group.column;
  }

  private patchLeadInColumn(
    status: CaseAcquisitionStatus,
    leadId: number,
    patch: Partial<MyCasesData>
  ) {
    const arr = this.leads.get(status) ?? [];
    const idx = arr.findIndex((l) => l.id === leadId);
    if (idx === -1) return;

    const nextArr = [...arr];
    nextArr[idx] = { ...nextArr[idx], ...patch };

    const map = new Map(this.leads);
    map.set(status, nextArr);
    this.leads = map;
  }

  private configureColumns() {
    Object.values(CaseAcquisitionStatus).forEach((status) => {
      if (status === CaseAcquisitionStatus.HISTORY) return;
      this.leads.set(status, []);
    });
  }

  private myCasesList() {
    this.loading = true;

    this.service
      .findUserCases()
      .pipe(
        catchError((err) => {
          console.log('Erro ao listar casos do usuário:', err);
          return EMPTY;
        }),
        finalize(() => (this.loading = false))
      )
      .subscribe(({ data }) => {
        const map = new Map(this.leads);
        // limpa as colunas (mantendo as chaves)
        for (const k of map.keys()) map.set(k, []);

        for (const lead of data) {
          if (lead.status === CaseAcquisitionStatus.HISTORY) continue;
          map.set(lead.status, [...(map.get(lead.status) ?? []), lead]);
        }

        this.leads = map;
      });
  }
}
